import { field } from '../fields'
import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Mail, MessageSquare } from 'lucide-react'
import {
  fetchTemplates,
  preview,
  saveTemplate,
  unknownPlaceholders,
  type NotificationTemplate,
  type TemplateEdit,
} from '../api/notificationTemplates'
/**
 * Every message the studio sends, in Tonya's own words (D-166).
 *
 * **The copy has always been data** (A-16) — what was missing was the door.
 * The capability existed with no endpoint and no screen, so changing a comma in
 * an email was a developer task, which is the exact support call A-16 exists to
 * prevent.
 *
 * **Placeholders are checked as she types.** An unknown one renders literally
 * in a real email, deliberately, because obviously broken beats "Hi ," which
 * looks almost right — but the best time to catch it is before it is saved, not
 * after a parent has read it.
 */
export default function NotificationTemplatesView() {
  const { data, isPending, isError } = useQuery({
    queryKey: ['notification-templates'],
    queryFn: fetchTemplates,
  })

  return (
    <div className="mx-auto w-full max-w-[46rem] px-5 py-8">
      <header className="mb-5">
        <p className="text-[11px] font-bold tracking-[0.14em] text-accent uppercase">Studio</p>
        <h1 className="font-display text-2xl">Messages the studio sends</h1>
        <p className="mt-1.5 max-w-[40rem] text-[13.5px] text-ink-3">
          Every automatic email, in your words. Anything in double braces &mdash; like
          <code className="mx-1 rounded bg-surface-2 px-1 text-[12.5px]">{'{{dancerName}}'}</code>
          &mdash; is filled in when the message is sent.
        </p>
      </header>

      {isPending && <p className="text-[13px] text-ink-3">Loading...</p>}
      {isError && <p className="text-[13px] text-crit">Could not load the messages.</p>}

      <div className="flex flex-col gap-2.5">
        {data?.map((template) => <TemplateRow key={template.templateId} template={template} />)}
      </div>
    </div>
  )
}

function TemplateRow({ template }: { template: NotificationTemplate }) {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)

  const [edit, setEdit] = useState<TemplateEdit>({
    emailSubject: template.emailSubject,
    emailBody: template.emailBody,
    smsBody: template.smsBody,
    isEnabled: template.isEnabled,
    sendsInApp: template.sendsInApp,
    sendsEmail: template.sendsEmail,
    sendsPush: template.sendsPush,
  })

  /*
   * Re-seeded when the server sends a newer copy back, so a save leaves the form
   * showing what was actually stored rather than what was typed.
   *
   * **Every field, and a missing one is not harmless.** `dirty` compares each
   * against the template and `undefined !== false`, so a field left out here
   * leaves the row permanently claiming unsaved changes with Save enabled before
   * anything has been touched. `sendsPush` was omitted for exactly one commit
   * and it was found by driving the component, not by reading it.
   */
  useEffect(() => {
    setEdit({
      emailSubject: template.emailSubject,
      emailBody: template.emailBody,
      smsBody: template.smsBody,
      isEnabled: template.isEnabled,
      sendsInApp: template.sendsInApp,
      sendsEmail: template.sendsEmail,
      sendsPush: template.sendsPush,
    })
  }, [template])

  const save = useMutation({
    mutationFn: () => saveTemplate(template.templateId, edit),
    onSuccess: (templates) => {
      queryClient.setQueryData(['notification-templates'], templates)
      setOpen(false)
    },
  })

  const unknown = unknownPlaceholders(template, edit.emailSubject, edit.emailBody, edit.smsBody)

  const dirty =
    edit.emailSubject !== template.emailSubject ||
    edit.emailBody !== template.emailBody ||
    edit.smsBody !== template.smsBody ||
    edit.isEnabled !== template.isEnabled ||
    edit.sendsInApp !== template.sendsInApp ||
    edit.sendsEmail !== template.sendsEmail ||
    edit.sendsPush !== template.sendsPush

  const set = (patch: Partial<TemplateEdit>) => setEdit((e) => ({ ...e, ...patch }))

  return (
    <div className="rounded-2xl border border-line bg-surface px-4 py-3.5">
      <div className="flex flex-wrap items-baseline gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-[14px] font-semibold">
            {template.name}
            {!template.isEnabled && (
              <span className="ml-2 rounded-md bg-surface-2 px-1.5 py-0.5 text-[11px] font-semibold text-ink-3">
                Off
              </span>
            )}
          </p>
          {template.description && (
            <p className="mt-0.5 text-[12.5px] text-ink-3">{template.description}</p>
          )}
        </div>

        {!open && (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-lg border border-line-2 px-3 py-1.5 text-[12.5px] font-semibold text-ink-2 hover:border-ink-3"
          >
            Edit wording
          </button>
        )}
      </div>

      {!open && template.emailSubject && (
        // The preview reads as a sentence rather than a template, so the shape
        // of what a parent receives is visible without opening anything.
        <p className="mt-2 text-[12.5px] text-ink-3 italic wrap-anywhere">
          {preview(template.emailSubject)}
        </p>
      )}

      {open && (
        <div className="mt-3 flex flex-col gap-3">
          <div className="flex flex-wrap gap-1.5">
            <span className="text-[12px] font-semibold text-ink-3">You can use:</span>
            {template.mergeFields.map((f) => (
              <code
                key={f}
                className="rounded bg-surface-2 px-1.5 py-0.5 text-[11.5px] text-ink-2"
              >
                {`{{${f}}}`}
              </code>
            ))}
          </div>

          <label className="flex flex-col gap-1">
            <span className="flex items-center gap-1.5 text-[12px] font-semibold text-ink-3">
              <Mail size={12} strokeWidth={2.5} />
              Subject line
            </span>
            <input
              value={edit.emailSubject ?? ''}
              onChange={(e) => set({ emailSubject: e.target.value || null })}
              className={field}
            />
          </label>

          <label className="flex flex-col gap-1">
            <span className="text-[12px] font-semibold text-ink-3">Email</span>
            <textarea
              value={edit.emailBody ?? ''}
              onChange={(e) => set({ emailBody: e.target.value || null })}
              rows={7}
              className={`${field} leading-relaxed`}
            />
          </label>

          {/*
            Shown only where the template already has one. SMS was removed from
            the plan (D-132), so offering an empty text-message box everywhere
            would advertise a channel the studio does not have.

            **It is no longer only for texting** (D-531): this same short line is
            what the bell shows and what appears on a phone. Renamed, because a
            label naming a channel the studio does not use would have her editing
            a line she believed nobody could read.
          */}
          {template.smsBody !== null && (
            <label className="flex flex-col gap-1">
              <span className="flex items-center gap-1.5 text-[12px] font-semibold text-ink-3">
                <MessageSquare size={12} strokeWidth={2.5} />
                Short version, for the bell and for phones
              </span>
              <textarea
                value={edit.smsBody ?? ''}
                onChange={(e) => set({ smsBody: e.target.value || null })}
                rows={3}
                className={field}
              />
            </label>
          )}

          <label className="flex items-center gap-2 text-[13px]">
            <input
              type="checkbox"
              checked={edit.isEnabled}
              onChange={(e) => set({ isEnabled: e.target.checked })}
              className="size-4 accent-[var(--color-accent)]"
            />
            Send this message
          </label>

          {/*
            Where it goes, which nothing could change until now (D-522).

            <b>Two switches rather than one control</b>, because they are not
            alternatives: most messages are both, and a message that is neither
            is a real thing to want — the enum says so out loud, and it is not
            the same as switching the message off above.

            Worded as places rather than as channel names. "In the app" is where
            the bell is, which is the thing Tonya recognises.
          */}
          <fieldset className="flex flex-wrap items-center gap-x-5 gap-y-2 border-0 p-0">
            <legend className="mb-1 text-[12px] font-semibold text-ink-3">Where it goes</legend>

            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={edit.sendsInApp ?? template.sendsInApp}
                onChange={(e) => set({ sendsInApp: e.target.checked })}
                className="size-4 accent-[var(--color-accent)]"
              />
              In the app
            </label>

            <label className="flex items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                checked={edit.sendsEmail ?? template.sendsEmail}
                onChange={(e) => set({ sendsEmail: e.target.checked })}
                className="size-4 accent-[var(--color-accent)]"
              />
              By email
            </label>

            {/*
              **The third place, and it depends on the first** (D-531). Push
              rides alongside the bell and never instead of it: everything that
              makes it safe to lose rests on a durable row having been written
              first (D-523), so switching the bell off takes this with it and
              the control says so rather than sitting there enabled and inert.
            */}
            <label
              className={`flex items-center gap-2 text-[13px] ${
                (edit.sendsInApp ?? template.sendsInApp) ? '' : 'opacity-50'
              }`}
            >
              <input
                type="checkbox"
                disabled={!(edit.sendsInApp ?? template.sendsInApp)}
                checked={
                  (edit.sendsInApp ?? template.sendsInApp) &&
                  (edit.sendsPush ?? template.sendsPush)
                }
                onChange={(e) => set({ sendsPush: e.target.checked })}
                className="size-4 accent-[var(--color-accent)]"
              />
              On phones
            </label>

            {(edit.sendsPush ?? template.sendsPush) &&
              (edit.sendsInApp ?? template.sendsInApp) && (
                <p className="basis-full text-[12.5px] text-ink-3">
                  {/* Two things she cannot see from here and would otherwise
                      report as a fault: only families who have switched it on
                      get one, and a body carrying a phone number or a decline
                      reason is left off the banner (D-528). */}
                  Only families who have turned notifications on for their own device get these,
                  and anything private in the short line is kept for the app.
                </p>
              )}

            {/*
              Said plainly rather than left to be discovered. Both off is
              allowed and is not an error — but it is not the same as the
              switch above, and somebody will otherwise wonder why a message
              they left enabled never arrived.
            */}
            {edit.sendsInApp === false && edit.sendsEmail === false && edit.isEnabled && (
              <p className="basis-full text-[12.5px] text-warn">
                This message is on, but has nowhere to go &mdash; nobody will see it.
              </p>
            )}
          </fieldset>

          {unknown.length > 0 && (
            <p role="alert" className="text-[12.5px] text-crit">
              {unknown.map((u) => `{{${u}}}`).join(', ')}{' '}
              {unknown.length === 1 ? 'is not something' : 'are not things'} this message knows
              about &mdash; it would print exactly like that in the email.
            </p>
          )}

          {save.isError && (
            <p role="alert" className="text-[12.5px] text-crit">
              {(save.error as Error).message}
            </p>
          )}

          {edit.emailBody && (
            <div className="rounded-lg border border-line bg-surface-2 px-3 py-2.5">
              <p className="mb-1 text-[11px] font-semibold tracking-[0.06em] text-ink-3 uppercase">
                Roughly what they will read
              </p>
              <p className="text-[12.5px] leading-relaxed whitespace-pre-wrap wrap-anywhere text-ink-2">
                {preview(edit.emailBody)}
              </p>
            </div>
          )}

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => save.mutate()}
              disabled={!dirty || unknown.length > 0 || save.isPending}
              className="rounded-lg bg-accent px-3.5 py-2 text-[12.5px] font-semibold text-on-accent disabled:opacity-40"
            >
              {save.isPending ? 'Saving...' : 'Save'}
            </button>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="text-[12.5px] font-semibold text-ink-3 hover:text-ink"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
